import { useEffect, useRef, useState, useCallback, type CSSProperties } from "react";
import { useIsMobile } from "../../lib/useIsMobile";
import { callFn, waitlistSetupList, waitlistSetupAction } from "../../lib/api";
import type { WaitlistEntry, WaitlistTable } from "../../lib/api";
import { useConfirmDialog } from "../../components/ConfirmDialog";
import UpcomingReservationsRail from "./UpcomingReservationsRail";
import ServiceDayCalendar from "../../components/host/ServiceDayCalendar";
import { tableFitsParty, remainingTableCapacity, minimumGuestsPresent, hasMinimumGuestsPresent, holdCountdown, notificationLabel, arrivalWindowFulfilled, finalCallLabel, canSendFinalCall } from "../../lib/waitlist-workflow";
import type { TableSetupAlert } from "../../types/intercom";

/* ── Types ── */
type ActiveStatus = "waiting" | "notified" | "final_call" | "seated";
type Action = "set_party_size" | "notify" | "manual_notify" | "arrive" | "extend_hold" | "final_call" | "seat" | "no_show" | "cancel" | "depart" | "transfer" | "set_move";

interface ListResponse {
  server_now?: string;
  business_day: string;
  entries: WaitlistEntry[];
  tables: WaitlistTable[];
}

/* ── Helpers ── */


function waitTime(createdAt: string, serverNow: number): string {
  const mins = Math.max(0, Math.floor((serverNow - Date.parse(createdAt)) / 60000));
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function displayName(e: WaitlistEntry): string {
  return e.first_name_at_intake
    ? `${e.first_name_at_intake} · ${e.party_size}`
    : `Party of ${e.party_size}`;
}

const ROOM_LABEL: Record<string, string> = {
  any: "Any", lounge: "Lounge", library: "Library", parlor: "Parlor",
};
const ROOM_COLOR: Record<string, string> = {
  lounge:  "#7EB8F7",
  parlor:  "#D49BC4",
  library: "#7EC8A4",
};

function parseIntentBadge(hostNotes: string | null): { emoji: string; label: string; color: string; notesWithoutIntent: string | null } {
  if (!hostNotes) return { emoji: "", label: "", color: "", notesWithoutIntent: null };
  const match = hostNotes.match(/^\[Intent:\s*([^\]]+)\]\s*(?:—\s*)?(.*)$/);
  if (!match) return { emoji: "", label: "", color: "", notesWithoutIntent: hostNotes };

  const rawLabel = match[1];
  const rest = match[2]?.trim() || null;

  let emoji = "🥃";
  let label = "Neat Pours";
  let color = "#7EC8A4"; // Library Green

  if (rawLabel.includes("Food")) {
    emoji = "🍽️";
    label = "Food & Cocktails";
    color = "#7EB8F7"; // Lounge Blue
  } else if (rawLabel.includes("Cocktails")) {
    emoji = "🍸";
    label = "Cocktails Only";
    color = "#D49BC4"; // Parlor Pink
  }

  return { emoji, label, color, notesWithoutIntent: rest };
}
const STATUS_ACTION_MAP: Record<ActiveStatus, Action[]> = {
  waiting:    ["notify", "seat", "cancel"],
  notified:   ["final_call", "seat", "no_show", "cancel"],
  final_call: ["seat", "no_show", "cancel"],
  seated:     ["transfer", "depart"],
};

/* ── Helpers ── */
function groupByRoom(list: WaitlistTable[]): Record<string, WaitlistTable[]> {
  return list.reduce<Record<string, WaitlistTable[]>>((acc, t) => {
    (acc[t.room_id] ??= []).push(t);
    return acc;
  }, {});
}

/* ── Entry card ── */
interface CardProps {
  serverNow: number;
  entry: WaitlistEntry;
  entries: WaitlistEntry[];
  tables: WaitlistTable[];
  busy: string | null;
  error: string | null;
  isSeatTarget: boolean;
  isTransferTarget: boolean;
  tableSetupStatus?: TableSetupAlert | null;
  onAction: (action: Action, tableId?: string, value?: number) => void;
  onSeatOpen: () => void;
  onSeatClose: () => void;
  onTransferOpen: () => void;
  onTransferClose: () => void;
  onRequestTableSetup?: () => void;
}

export function EntryCard({
  entry, entries, tables, busy, error, serverNow,
  isSeatTarget, isTransferTarget, tableSetupStatus,
  onAction, onSeatOpen, onSeatClose, onTransferOpen, onTransferClose,
  onRequestTableSetup,
}: CardProps) {
  const [notifyPicker, setNotifyPicker] = useState<"notify" | "manual_notify" | null>(null);
  const status = entry.status as ActiveStatus;
  const actions = STATUS_ACTION_MAP[status] ?? [];

  // Determine room color for the left accent strip
  const seatedTable = status === "seated" ? tables.find((t) => t.id === entry.seated_table_id) : null;
  const accentRoom = seatedTable?.room_id ?? (entry.preferred_room !== "any" ? entry.preferred_room : null);
  const leftAccent = accentRoom ? ROOM_COLOR[accentRoom] : null;

  const fits = (table: WaitlistTable) => tableFitsParty(table, entries, entry.party_size, entry.host_notes, entry.id);
  const seatByRoom = groupByRoom(
    tables.filter(t => fits(t) && (!entry.held_table_id || t.id === entry.held_table_id)).map(t => ({ ...t, label: `${t.label} - ${remainingTableCapacity(t, entries, entry.id)} seats free` }))
  );
  const transferByRoom = groupByRoom(
    tables.filter(
      (t) => fits(t) && t.id !== entry.seated_table_id
    )
  );

  const { emoji, label: intentLabel, color: intentColor, notesWithoutIntent } = parseIntentBadge(entry.host_notes);
  const countdown = holdCountdown(entry, serverNow);
  const notification = notificationLabel(entry);
  const finalCall = finalCallLabel(entry);
  const expired = !!entry.hold_expires_at && Date.parse(entry.hold_expires_at) <= serverNow && !arrivalWindowFulfilled(entry);
  const allowZero = entry.source === "reservation" || (!entry.source && !!entry.reservation_id);
  const enoughGuests = hasMinimumGuestsPresent(entry.party_size, entry.arrived_count ?? 0);
  const seatUnavailable = !!busy || !enoughGuests || expired;
  const requestedLabel = entry.metadata?.requested_seating_label;
  const requestedSeating = typeof requestedLabel === "string" && requestedLabel.trim()
    ? requestedLabel.trim()
    : tables.find(table => table.id === entry.metadata?.requested_table_id)?.label;

  return (
    <div style={{ ...styles.card, ...(error ? styles.cardError : {}), ...(leftAccent ? { borderLeft: `3px solid ${leftAccent}` } : {}) }}>
      {/* Header row */}
      <div style={styles.cardHeader}>
        <span style={styles.cardName}>{displayName(entry)}</span>
        <span style={styles.waitBadge}>{waitTime(entry.requested_at || entry.queue_activated_at || entry.created_at, serverNow)}</span>
      </div>

      {/* Meta row: chips + inline Move/Depart for seated parties */}
      <div style={styles.cardMetaRow}>
        <div style={styles.cardMeta}>
          {intentLabel && (
            <span style={{
              ...styles.metaChip,
              color: intentColor,
              borderColor: `${intentColor}55`,
              background: `${intentColor}14`,
              fontWeight: 800,
            }}>
              {emoji} {intentLabel}
            </span>
          )}
          {status === "seated" ? (
            (() => {
              const t = tables.find((tbl) => tbl.id === entry.seated_table_id);
              if (!t) return null;
              const rc = ROOM_COLOR[t.room_id] ?? "#4CAF50";
              return (
                <>
                  <span style={{ ...styles.metaChip, color: rc, borderColor: `${rc}55`, background: `${rc}12`, fontWeight: 800 }}>
                    {ROOM_LABEL[t.room_id] ?? t.room_id}
                  </span>
                  <span style={{ ...styles.metaChip, color: rc, borderColor: `${rc}55`, background: `${rc}0D` }}>
                    {t.label}
                  </span>
                </>
              );
            })()
          ) : (
            entry.preferred_room !== "any" && (
              <span style={styles.metaChip}>{ROOM_LABEL[entry.preferred_room]}</span>
            )
          )}
          {entry.move_to_room && (
            <span style={{ ...styles.metaChip, ...styles.chipMove }}>
              → {ROOM_LABEL[entry.move_to_room]}
            </span>
          )}
          {entry.c33_priority && <span style={{ ...styles.metaChip, ...styles.chipGold }}>C33</span>}
          {entry.celebration_notes && <span style={{ ...styles.metaChip, ...styles.chipCelebration }}>🎉</span>}
          {entry.parlor_holdover_ok && status !== "seated" &&
            entry.preferred_room !== "any" && entry.preferred_room !== "parlor" &&
            !entry.move_to_room && (
            <span style={{ ...styles.metaChip, ...styles.chipParlorOk }}>Parlor OK</span>
          )}
          {tableSetupStatus?.status === "setup_requested" && (
            <span style={{ fontSize: 11, fontWeight: 800, color: "#E05040", background: "rgba(224,80,64,0.12)", border: "1px solid rgba(224,80,64,0.4)", borderRadius: 6, padding: "2px 8px" }}>
              🛎️ Bar Alerted
            </span>
          )}
          {tableSetupStatus?.status === "server_alerted" && (
            <span style={{ fontSize: 11, fontWeight: 800, color: "#F9AF36", background: "rgba(249,175,54,0.12)", border: "1px solid rgba(249,175,54,0.4)", borderRadius: 6, padding: "2px 8px" }}>
              🟡 Server Setting Table
            </span>
          )}
          {tableSetupStatus?.status === "table_ready" && (
            <span style={{ fontSize: 11, fontWeight: 800, color: "#27ae60", background: "rgba(39,174,96,0.15)", border: "1px solid #27ae60", borderRadius: 6, padding: "2px 8px" }}>
              🟢 Table Ready! Seat Guests
            </span>
          )}
        </div>
        {status === "seated" && !isSeatTarget && !isTransferTarget && (
          <div style={styles.seatedInlineActions}>
            {actions.includes("transfer") && (
              <button type="button" style={styles.moveBtn} disabled={!!busy || !enoughGuests} onClick={onTransferOpen}>
                Move
              </button>
            )}
            {actions.includes("depart") && (
              <button type="button" style={styles.primaryBtn} disabled={!!busy}
                aria-busy={busy === "depart"} aria-label="Depart"
                onClick={() => onAction("depart")}>
                {busy === "depart" ? "…" : "Depart"}
              </button>
            )}
          </div>
        )}
      </div>

      {entry.celebration_notes && (
        <p style={styles.noteText}>{entry.celebration_notes}</p>
      )}
      {notesWithoutIntent && (
        <p style={styles.noteText}>{notesWithoutIntent}</p>
      )}
      {status !== "seated" && requestedSeating && <p style={{ ...styles.noteText, fontStyle: "normal" }}>
        Requested seating: {requestedSeating} · Preference only, subject to availability.
      </p>}

      {error && <p style={styles.cardErrorMsg}>{error}</p>}
      {notification && <p role="status" style={{ ...styles.noteText, fontStyle: "normal" }}>{notification}</p>}
      {finalCall && <p role="status" style={{ ...styles.noteText, fontStyle: "normal" }}>{finalCall}</p>}
      {countdown && <p style={{ ...styles.noteText, fontStyle: "normal", color: expired ? "#E05040" : "var(--gold)", fontWeight: 800 }}>{tables.find(t => t.id === entry.held_table_id)?.label || "Held table"} · {countdown}</p>}
      {status !== "seated" && <div style={styles.cardActions}>
        <label style={{ ...styles.noteText, fontStyle: "normal" }}>Party size: <select style={{ minHeight: 44, minWidth: 70, color: "var(--text)", background: "var(--dark-1)", border: "1px solid var(--border)", borderRadius: 6 }} aria-label={`Party size for ${displayName(entry)}`} value={entry.party_size} disabled={!!busy} onChange={event => {
          const size = Number(event.target.value);
          if (Number.isInteger(size) && size >= Math.max(1, entry.arrived_count ?? 0) && size <= 30 && size !== entry.party_size) onAction("set_party_size", undefined, size);
        }}>
          {Array.from({ length: 30 }, (_, index) => index + 1).filter(size => size >= (entry.arrived_count ?? 0)).map(size => <option key={size} value={size}>{size}</option>)}
        </select></label>
        <label style={{ ...styles.noteText, fontStyle: "normal" }}>Guests present: <select style={{ minHeight: 44, minWidth: 85, color: "var(--text)", background: "var(--dark-1)", border: "1px solid var(--border)", borderRadius: 6 }} aria-label={`Guests present for ${displayName(entry)}`} value={entry.arrived_count ?? 0} disabled={!!busy} onChange={event => onAction("arrive", undefined, Number(event.target.value))}>
          {!allowZero && (entry.arrived_count ?? 0) === 0 && <option value={0} disabled>Not recorded</option>}
          {Array.from({ length: entry.party_size + (allowZero ? 1 : 0) }, (_, index) => { const count = index + (allowZero ? 0 : 1); return <option key={count} value={count}>{count} of {entry.party_size}</option>; })}
        </select></label>
        {entry.hold_expires_at && !arrivalWindowFulfilled(entry) && <button type="button" style={styles.ghostBtn} disabled={!!busy} onClick={() => onAction("extend_hold", undefined, 10)}>Extend 10 minutes</button>}
      </div>}

      <p style={{ ...styles.noteText, fontStyle: "normal" }}>At least {minimumGuestsPresent(entry.party_size)} of {entry.party_size} guests must be present to seat or move. The entire party is expected.</p>

      {/* Action buttons — seated party buttons live inline in the meta row above */}
      {status !== "seated" && !isSeatTarget && !isTransferTarget && (
        <div style={styles.cardActions}>
          {onRequestTableSetup && entry.held_table_id && (
            <button
              type="button"
              style={{
                ...styles.ghostBtn,
                color: tableSetupStatus ? "var(--gold)" : "var(--muted)",
                borderColor: tableSetupStatus ? "var(--gold)" : "var(--border)",
                background: tableSetupStatus ? "rgba(249,175,54,0.08)" : "transparent",
              }}
              onClick={onRequestTableSetup}
              disabled={!!busy}
              title="Request setup for the held table"
            >
              {tableSetupStatus ? "🛎️ Re-alert Bar" : "🛎️ Alert Bar"}
            </button>
          )}
          {actions.includes("notify") && (
            <button type="button" style={styles.primaryBtn} disabled={!!busy || ["unknown", "pending", "sending"].includes(entry.notification_status || "")}
              aria-busy={busy === "notify"} aria-label="Notify"
              onClick={() => setNotifyPicker("notify")}>
              {busy === "notify" ? "…" : "Notify"}
            </button>
          )}
          {(!entry.notified_at || ["delivery_failed", "unknown"].includes(entry.notification_status || "")) && <button type="button" style={styles.ghostBtn} disabled={!!busy} onClick={() => setNotifyPicker("manual_notify")}>Record phone / in-person notification</button>}
          {canSendFinalCall(entry) && (
            <button type="button" style={styles.primaryBtn} disabled={!!busy}
              aria-busy={busy === "final_call"} aria-label={entry.final_call_status ? "Retry Final Call" : "Final Call"}
              onClick={() => onAction("final_call")}>
              {busy === "final_call" ? "…" : entry.final_call_status ? "Retry Final Call" : "Final Call"}
            </button>
          )}
          {actions.includes("seat") && (
            <button type="button" style={{ ...styles.seatBtn, opacity: seatUnavailable ? 0.45 : 1 }} disabled={seatUnavailable}
              title={expired ? "Extend or release the expired hold" : `At least ${minimumGuestsPresent(entry.party_size)} of ${entry.party_size} guests must be present`}
              onClick={onSeatOpen}>
              Seat
            </button>
          )}
          {actions.includes("no_show") && (entry.arrived_count ?? 0) < entry.party_size && (
            <button type="button" style={styles.ghostBtn} disabled={!!busy}
              aria-busy={busy === "no_show"} aria-label="No Show"
              onClick={() => onAction("no_show")}>
              {busy === "no_show" ? "…" : "No Show"}
            </button>
          )}
        </div>
      )}
      {status !== "seated" && !isSeatTarget && !isTransferTarget && actions.includes("cancel") && (
        <div style={{ marginTop: 16 }}>
          <button type="button" style={{ ...styles.ghostBtn, color: "#FF8A7A", borderColor: "#FF8A7A" }} disabled={!!busy}
            aria-busy={busy === "cancel"} aria-label="Cancel"
            onClick={() => onAction("cancel")}>
            {busy === "cancel" ? "…" : "Cancel"}
          </button>
        </div>
      )}

      {notifyPicker && <TablePicker title={notifyPicker === "notify" ? "Hold table and notify guest" : "Choose contacted guest's table"}
        tablesByRoom={seatByRoom} busy={busy} busyPrefix={notifyPicker}
        onPick={tableId => { onAction(notifyPicker, tableId); setNotifyPicker(null); }} onClose={() => setNotifyPicker(null)} />}

      {/* Inline seat picker */}
      {isSeatTarget && (
        <TablePicker
          title="Pick a table"
          tablesByRoom={seatByRoom}
          busy={seatUnavailable ? "unavailable" : busy}
          onPick={(tableId) => { if (!seatUnavailable) onAction("seat", tableId); }}
          onClose={onSeatClose}
          busyPrefix="seat"
        />
      )}

      {/* Inline transfer picker */}
      {isTransferTarget && (
        <div>
          <TablePicker
            title="Move to table"
            tablesByRoom={transferByRoom}
            busy={!enoughGuests ? "unavailable" : busy}
            onPick={(tableId) => { if (enoughGuests) onAction("transfer", tableId); }}
            onClose={onTransferClose}
            busyPrefix="transfer"
          />
          {/* Quick clear move target */}
          {entry.move_to_room && (
            <button type="button"
              style={{ ...styles.ghostBtn, marginTop: 6, width: "100%", fontSize: 11 }}
              disabled={!!busy}
              onClick={() => onAction("set_move")}>
              Clear move target
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Table picker (shared between Seat and Transfer) ── */
interface TablePickerProps {
  title: string;
  tablesByRoom: Record<string, WaitlistTable[]>;
  busy: string | null;
  onPick: (tableId: string) => void;
  onClose: () => void;
  busyPrefix: string;
}
function TablePicker({ title, tablesByRoom, busy, onPick, onClose, busyPrefix }: TablePickerProps) {
  const hasAny = Object.values(tablesByRoom).some((t) => t.length > 0);
  return (
    <div style={styles.seatPicker}>
      <div style={styles.seatPickerHeader}>
        <span style={styles.seatPickerTitle}>{title}</span>
        <button type="button" onClick={onClose} style={styles.seatPickerClose}>×</button>
      </div>
      {!hasAny ? (
        <p style={{ margin: 0, fontSize: 12, color: "var(--muted)", fontStyle: "italic", textAlign: "center", padding: "8px 0" }}>
          No available tables
        </p>
      ) : Object.entries(tablesByRoom).map(([roomId, roomTables]) =>
        roomTables.length === 0 ? null : (
          <div key={roomId} style={styles.tableGroup}>
            <p style={styles.tableGroupLabel}>{ROOM_LABEL[roomId] ?? roomId}</p>
            <div style={styles.tableGrid}>
              {roomTables.map((t) => {
                const rc = ROOM_COLOR[roomId] ?? "#4CAF50";
                return (
                  <button key={t.id} type="button"
                    style={{ ...styles.tableChip, border: `1px solid ${rc}`, background: `${rc}14`, color: rc }}
                    disabled={!!busy}
                    onClick={() => onPick(t.id)}>
                    {busy === `${busyPrefix}:${t.id}` ? "…" : t.label}
                    <span style={styles.tableCapacity}>1–{t.max_capacity}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )
      )}
    </div>
  );
}

/* ── Column ── */
function Column({ title, entries, color, children, stacked }: {
  title: string;
  entries: WaitlistEntry[];
  color: string;
  children: React.ReactNode;
  stacked?: boolean;
}) {
  return (
    <div style={stacked ? { ...styles.column, ...styles.columnStacked } : styles.column}>
      <div style={styles.columnHeader}>
        <span style={{ ...styles.columnTitle, color }}>{title}</span>
        <span style={styles.columnCount}>{entries.length}</span>
      </div>
      <div style={styles.columnBody}>{children}</div>
    </div>
  );
}

/* ── Main dashboard ── */
export default function HostDashboard() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [tables, setTables] = useState<WaitlistTable[]>([]);
  const [businessDay, setBusinessDay] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [pollError, setPollError] = useState<string | null>(null);

  // Per-entry UI state
  const [busy, setBusy] = useState<Record<string, string>>({});
  const [actionErrors, setActionErrors] = useState<Record<string, string>>({});
  const [seatTarget, setSeatTarget] = useState<string | null>(null);
  const [transferTarget, setTransferTarget] = useState<string | null>(null);
  const [roomFilter, setRoomFilter] = useState<string | null>(null);
  const [setups, setSetups] = useState<Record<string, TableSetupAlert>>({});
  const isMobile = useIsMobile(768);
  const narrowStatus = useIsMobile(1024);
  const [serverOffset, setServerOffset] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const [setupError, setSetupError] = useState<string | null>(null);
  const [chosenDay, setChosenDay] = useState<string | null>(null);
  const [requestCounts, setRequestCounts] = useState<Record<string, number> | null>(null);
  const [requestCount, setRequestCount] = useState<number | null>(null);
  useEffect(() => {
    if (chosenDay && /^\d{4}-\d{2}-\d{2}$/.test(businessDay) && !Number.isNaN(Date.parse(`${businessDay}T00:00:00Z`)) && new Date(`${businessDay}T00:00:00Z`).toISOString().slice(0, 10) === businessDay && chosenDay < businessDay) {
      setChosenDay(null);
      setRequestCount(null);
    }
  }, [businessDay, chosenDay]);
  const actionLocks = useRef(new Set<string>());
  const pollSequence = useRef(0);
  const setupRevision = useRef(0);

  const mountedRef = useRef(true);
  // Mutations and follow-up snapshots own status changes; polling never creates notification success.
  const inFlightRef = useRef(0);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const poll = useCallback(async (quiet = false) => {
    try {
      const sequence = ++pollSequence.current;
      const data = await callFn<ListResponse>("waitlist-host-list", {});
      if (!mountedRef.current || sequence !== pollSequence.current) return;
      if (data.server_now && Number.isFinite(Date.parse(data.server_now))) setServerOffset(Date.parse(data.server_now) - Date.now());
      if (inFlightRef.current > 0) {
        // Wait for the mutation's follow-up snapshot.
        setBusinessDay(data.business_day);
        setTables(data.tables);
        setPollError(null);
        if (!quiet) setLoading(false);
        return;
      }
      setEntries(data.entries);
      setTables(data.tables);
      setBusinessDay(data.business_day);
      setPollError(null);
      if (!quiet) setLoading(false);
    } catch {
      if (mountedRef.current) {
        setPollError("Connection issue — retrying…");
        if (!quiet) setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    poll(false);
    const timer = setInterval(() => poll(true), 5000);

    const clockTimer = setInterval(() => setClock(Date.now()), 1000);
    let cancelled = false;
    let setupPending = false;
    const refreshSetup = async () => {
      if (setupPending) return;
      setupPending = true;
      const revision = setupRevision.current;
      try {
        const data = await waitlistSetupList();
        if (!cancelled && mountedRef.current && revision === setupRevision.current) {
          setSetups(Object.fromEntries(data.alerts.map(alert => [alert.source_id || alert.id, alert])));
          setSetupError(null);
        }
      } catch {
        if (!cancelled && mountedRef.current) setSetupError("Table setup status is stale - retrying.");
      } finally { setupPending = false; }
    };
    void refreshSetup();
    const setupTimer = setInterval(refreshSetup, 10000);
    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearInterval(timer);
      clearInterval(clockTimer);
      clearInterval(setupTimer);
    };
  }, [poll]);

  const handleRequestTableSetup = async (entry: WaitlistEntry) => {
    if (actionLocks.current.has(entry.id)) return;
    actionLocks.current.add(entry.id);
    ++setupRevision.current;
    setBusy(previous => ({ ...previous, [entry.id]: "setup" }));
    setActionErrors(previous => { const next = { ...previous }; delete next[entry.id]; return next; });
    try {
      const { alert } = await waitlistSetupAction("request", entry.id, entry.held_table_id);
      if (mountedRef.current) setSetups(previous => ({ ...previous, [entry.id]: alert }));
    } catch (error) {
      if (mountedRef.current) setActionErrors(previous => ({ ...previous, [entry.id]: (error as Error).message || "Setup request failed." }));
    } finally {
      ++setupRevision.current;
      actionLocks.current.delete(entry.id);
      if (mountedRef.current) setBusy(previous => { const next = { ...previous }; delete next[entry.id]; return next; });
    }
  };

  async function handleAction(entryId: string, action: Action, tableId?: string, value?: number) {
    if (actionLocks.current.has(entryId)) return;
    const target = entries.find(entry => entry.id === entryId);
    if (!target) return;
    if (action === "manual_notify" || action === "extend_hold") {
      const ok = await confirm({
        title: action === "manual_notify" ? "Guest has been notified?" : "Extend this hold?",
        message: action === "manual_notify"
          ? (target.notified_at ? "Confirm you have spoken with the guest by phone or in person. Their original arrival deadline stays unchanged." : "Confirm you have spoken with the guest by phone or in person. This starts their entire party's 10-minute arrival window now.")
          : "Add 10 minutes to this table hold. The extension is recorded; the original notification time is preserved.",
        confirmLabel: action === "manual_notify" ? "Contact confirmed" : "Extend hold",
      });
      if (!ok) return;
    }
    // Confirm destructive actions — these were one-tap on the busy host
    // stand right next to Notify / Seat, and a mis-tap silently dropped
    // a waiting guest.
    if (action === "cancel") {
      const target = entries.find((e) => e.id === entryId);
      const who = target ? displayName(target) : "this party";
      const ok = await confirm({
        title: `Cancel ${who}?`,
        message: target ? `Remove ${target.first_name_at_intake || "this guest"}, party of ${target.party_size}, from the waitlist and release any held table?` : "Remove this party from the waitlist and release any held table?",
        confirmLabel: "Cancel party",
        cancelLabel: "Keep on waitlist",
        danger: true,
      });
      if (!ok) return;
    } else if (action === "no_show") {
      const target = entries.find((e) => e.id === entryId);
      const who = target ? displayName(target) : "this party";
      const ok = await confirm({
        title: `Mark ${who} as no-show?`,
        message: "Removes them from the active list.",
        confirmLabel: "No-Show",
        cancelLabel: "Cancel",
        danger: true,
      });
      if (!ok) return;
    }

    if (actionLocks.current.has(entryId)) return;
    actionLocks.current.add(entryId);
    inFlightRef.current += 1;
    ++pollSequence.current;
    setBusy(previous => ({ ...previous, [entryId]: tableId ? `${action}:${tableId}` : action }));
    setActionErrors(previous => { const next = { ...previous }; delete next[entryId]; return next; });
    try {
      const payload: Record<string, unknown> = { action, entry_id: entryId };
      if (tableId) payload.table_id = tableId;
      if (action === "set_move") payload.move_to_room = null;
      if (action === "arrive") payload.arrived_count = value;
      if (action === "set_party_size") payload.party_size = value;
      if (action === "extend_hold") payload.minutes = value;
      const result = await callFn<{ ok: boolean; entry?: WaitlistEntry; sms?: { sms_ok?: boolean; sms_skipped?: boolean; sms_error?: string; notification_status?: string } }>("waitlist-host-api", payload);
      if (!mountedRef.current) return;
      if (!result.ok) throw new Error("Action did not complete.");
      if (result.entry) setEntries(previous => previous.map(entry => entry.id === entryId ? result.entry! : entry));
      if (action === "set_party_size") setSetups(previous => { const next = { ...previous }; delete next[entryId]; return next; });
      if (result.sms && !result.sms.sms_ok) {
        setActionErrors(previous => ({ ...previous, [entryId]: result.sms?.sms_error || (result.sms?.sms_skipped ? "Text skipped - guest has not been notified by text." : "Text not confirmed - check notification status before retrying.") }));
      }
      if (action === "seat") setSeatTarget(null);
      if (action === "transfer") setTransferTarget(null);
    } catch (error) {
      if (mountedRef.current) setActionErrors(previous => ({ ...previous, [entryId]: (error as Error).message || "Action failed." }));
    } finally {
      actionLocks.current.delete(entryId);
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      if (mountedRef.current) {
        setBusy(previous => { const next = { ...previous }; delete next[entryId]; return next; });
        void poll(true);
      }
    }
  }

  if (loading) {
    return (
      <div style={styles.loading}>
        <p style={{ color: "var(--muted)", fontSize: 13 }}>Loading waitlist…</p>
      </div>
    );
  }

  const waiting    = entries.filter((e) => e.status === "waiting");
  const alerted    = entries.filter((e) => e.status === "notified" || e.status === "final_call");
  const seated     = entries.filter((e) => e.status === "seated");
  const pendingMoves = seated.filter((e) => e.move_to_room).length;
  const candidateDay = chosenDay && (!businessDay || chosenDay >= businessDay) ? chosenDay : businessDay;
  const parsedDay = /^\d{4}-\d{2}-\d{2}$/.test(candidateDay) ? new Date(`${candidateDay}T00:00:00Z`) : null;
  const validBusinessDay = /^\d{4}-\d{2}-\d{2}$/.test(businessDay) && !Number.isNaN(Date.parse(`${businessDay}T00:00:00Z`)) && new Date(`${businessDay}T00:00:00Z`).toISOString().slice(0, 10) === businessDay;
  const selectedDay = validBusinessDay && parsedDay && !Number.isNaN(parsedDay.getTime()) && parsedDay.toISOString().slice(0, 10) === candidateDay ? candidateDay : "";
  const futureView = !!selectedDay && selectedDay !== businessDay;

  // Room filter — waiting/notified filter by preferred_room (include "any"); seated by seated_room_id
  const ROOM_KEYS = ["lounge", "parlor", "library"] as const;
  const dispWaiting = roomFilter
    ? waiting.filter((e) => e.preferred_room === roomFilter || e.preferred_room === "any")
    : waiting;
  const dispAlerted = roomFilter
    ? alerted.filter((e) => e.preferred_room === roomFilter || e.preferred_room === "any")
    : alerted;
  const dispSeated = roomFilter
    ? seated.filter((e) => (e as any).seated_room_id === roomFilter)
    : seated;

  return (
    <div style={styles.shell}>
      {/* Status bar */}
      <div style={styles.statusBar}>
        <div style={{ ...styles.statusRow, ...(narrowStatus ? styles.statusRowNarrow : {}) }}>
          <div style={styles.roomPills} role="group" aria-label="Filter waitlist by room">
            {ROOM_KEYS.map((r) => {
              const active = roomFilter === r;
              const rc = ROOM_COLOR[r];
              return (
                <button
                  key={r}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setRoomFilter(active ? null : r)}
                  style={{
                    ...styles.roomPill,
                    ...(isMobile ? styles.roomPillNarrow : {}),
                    ...(active ? { border: `2px solid ${rc}`, background: `${rc}18`, color: rc } : {}),
                  }}
                >
                  {ROOM_LABEL[r]}
                </button>
              );
            })}
          </div>
          <div style={{ ...styles.dayLabel, ...(narrowStatus ? styles.dayLabelNarrow : {}) }}>
            <ServiceDayCalendar today={businessDay} selected={selectedDay} counts={requestCounts} onSelect={day => { setRequestCount(null); setChosenDay(day); }} />
            {isMobile && chosenDay !== null && selectedDay !== businessDay && <button type="button" style={styles.todayButton} onClick={() => { setRequestCount(null); setChosenDay(null); }}>Today</button>}
          </div>
          <div style={styles.statusRight}>{!isMobile && chosenDay !== null && selectedDay !== businessDay && <button type="button" style={styles.todayButton} onClick={() => { setRequestCount(null); setChosenDay(null); }}>Today</button>}<span style={styles.totalBadge}>{futureView ? (requestCount === null ? "Requests —" : `${requestCount} requests`) : `${entries.length} active`}</span></div>
        </div>
        {(pollError || pendingMoves > 0) && (
          <div style={styles.statusAlerts}>
            {pollError && <span style={styles.pollError}>{pollError}</span>}
            {pendingMoves > 0 && <span style={styles.moveAlert}>{pendingMoves} pending transfer{pendingMoves !== 1 ? "s" : ""}</span>}
          </div>
        )}
      </div>

      {/* Upcoming reservations rail — "Arrived" bridges a booking into the ledger below */}
      <UpcomingReservationsRail selectedDay={selectedDay} todayDay={businessDay} roomFilter={roomFilter} onCounts={setRequestCounts} onRequestCount={setRequestCount} onArrived={() => void poll(true)} />
      {!futureView && setupError && <p role="alert" style={styles.cardErrorMsg}>{setupError}</p>}

      {/* 3-column ledger — stacks vertically on phone/iPad-portrait */}
      {!futureView && <div style={isMobile ? styles.ledgerStacked : styles.ledger}>
        {/* Waiting */}
        <Column title="Waiting" entries={dispWaiting} color="var(--text)" stacked={isMobile}>
          {dispWaiting.length === 0 && <EmptyState text="No one waiting" />}
          {dispWaiting.map((e) => (
            <EntryCard
              key={e.id} entry={e} entries={entries} tables={tables} serverNow={clock + serverOffset}
              busy={busy[e.id] ?? null} error={actionErrors[e.id] ?? null}
              isSeatTarget={seatTarget === e.id}
              isTransferTarget={false}
              tableSetupStatus={setups[e.id] ?? null}
              onAction={(action, tableId, value) => handleAction(e.id, action, tableId, value)}
              onSeatOpen={() => setSeatTarget(e.id)}
              onSeatClose={() => setSeatTarget(null)}
              onTransferOpen={() => {}}
              onTransferClose={() => {}}
              onRequestTableSetup={() => handleRequestTableSetup(e)}
            />
          ))}
        </Column>

        {/* Notified / Final Call */}
        <Column title="Notified" entries={dispAlerted} color="#4CAF50" stacked={isMobile}>
          {dispAlerted.length === 0 && <EmptyState text="None notified" />}
          {dispAlerted.map((e) => (
            <EntryCard
              key={e.id} entry={e} entries={entries} tables={tables} serverNow={clock + serverOffset}
              busy={busy[e.id] ?? null} error={actionErrors[e.id] ?? null}
              isSeatTarget={seatTarget === e.id}
              isTransferTarget={false}
              tableSetupStatus={setups[e.id] ?? null}
              onAction={(action, tableId, value) => handleAction(e.id, action, tableId, value)}
              onSeatOpen={() => setSeatTarget(e.id)}
              onSeatClose={() => setSeatTarget(null)}
              onTransferOpen={() => {}}
              onTransferClose={() => {}}
              onRequestTableSetup={() => handleRequestTableSetup(e)}
            />
          ))}
        </Column>

        {/* Seated */}
        <Column title="Seated" entries={dispSeated} color="var(--gold)" stacked={isMobile}>
          {dispSeated.length === 0 && <EmptyState text="No one seated" />}
          {dispSeated.map((e) => (
            <EntryCard
              key={e.id} entry={e} entries={entries} tables={tables} serverNow={clock + serverOffset}
              busy={busy[e.id] ?? null} error={actionErrors[e.id] ?? null}
              isSeatTarget={false}
              isTransferTarget={transferTarget === e.id}
              onAction={(action, tableId, value) => handleAction(e.id, action, tableId, value)}
              onSeatOpen={() => {}}
              onSeatClose={() => {}}
              onTransferOpen={() => { setSeatTarget(null); setTransferTarget(e.id); }}
              onTransferClose={() => setTransferTarget(null)}
            />
          ))}
        </Column>
      </div>}
      {confirmDialog}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p style={styles.emptyState}>{text}</p>;
}

const styles: Record<string, CSSProperties> = {
  shell: { display: "flex", flexDirection: "column", gap: 12, height: "100%", marginTop: -8 },
  loading: {
    minHeight: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statusBar: {
    padding: "0 0 6px",
    borderBottom: "1px solid var(--border)",
  },
  statusRow: { display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 },
  statusRowNarrow: { gridTemplateColumns: "1fr auto" },
  statusAlerts: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, paddingTop: 6, fontSize: 12 },
  dayLabel: { display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", gap: 4, textAlign: "center" },
  statusRight: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 },
  todayButton: { minHeight: 44, padding: "4px 8px", border: "1px solid var(--border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)", cursor: "pointer" },
  dayLabelNarrow: { gridColumn: "1 / -1", gridRow: 1 },
  pollError: { color: "#FF8A7A", fontWeight: 600 },
  moveAlert: {
    padding: "2px 10px",
    borderRadius: 8,
    background: "rgba(255,152,0,0.12)",
    border: "1px solid rgba(255,152,0,0.4)",
    color: "#FF9800",
    fontWeight: 800,
  },
  roomPills: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  roomPill: {
    minHeight: 48,
    minWidth: 90,
    padding: "10px 18px",
    borderRadius: 8,
    border: "2px solid var(--border)",
    background: "transparent",
    color: "var(--muted)",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: 0.3,
  },
  roomPillNarrow: { minWidth: 0, padding: "10px 8px" },
  roomPillActive: {
    border: "1px solid var(--gold)",
    background: "rgba(249,175,54,0.12)",
    color: "var(--gold)",
  },
  totalBadge: {
    justifySelf: "end",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 16,
    whiteSpace: "nowrap",
    fontWeight: 700,
    color: "var(--muted)",
  },
  ledger: {
    display: "flex",
    gap: 12,
    flex: 1,
    minHeight: 0,
    overflowX: "auto",
  },
  ledgerStacked: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  columnStacked: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
  },
  column: {
    flex: "1 1 280px",
    minWidth: 240,
    display: "flex",
    flexDirection: "column",
    gap: 0,
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 12,
    overflow: "hidden",
  },
  columnHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 14px",
    borderBottom: "1px solid var(--border)",
  },
  columnTitle: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.7,
  },
  columnCount: {
    fontSize: 18,
    fontWeight: 800,
    color: "var(--muted)",
  },
  columnBody: {
    flex: 1,
    overflowY: "auto",
    padding: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  emptyState: {
    margin: "16px 0",
    textAlign: "center",
    color: "var(--muted)",
    fontSize: 12,
    fontStyle: "italic",
  },
  card: {
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  cardError: {
    borderColor: "rgba(224, 80, 64, 0.5)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  cardName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--text)",
    flex: 1,
  },
  waitBadge: {
    fontSize: 18,
    fontWeight: 800,
    color: "var(--text)",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    letterSpacing: -0.5,
    flexShrink: 0,
  },
  cardMetaRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "nowrap",
  },
  cardMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    flex: 1,
    minWidth: 0,
  },
  seatedInlineActions: {
    display: "flex",
    gap: 5,
    flexShrink: 0,
    marginLeft: "auto",
  },
  metaChip: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    padding: "2px 7px",
    borderRadius: 6,
    border: "1px solid var(--border)",
    color: "var(--muted)",
  },
  chipGold: {
    color: "var(--gold)",
    borderColor: "rgba(249,175,54,0.4)",
    background: "rgba(249,175,54,0.06)",
  },
  chipSeated: {
    color: "#4CAF50",
    borderColor: "rgba(76,175,80,0.4)",
    background: "rgba(76,175,80,0.06)",
    fontWeight: 800,
  },
  chipMove: {
    color: "#FF9800",
    borderColor: "rgba(255,152,0,0.4)",
    background: "rgba(255,152,0,0.08)",
    fontWeight: 800,
  },
  chipParlorOk: {
    color: "var(--muted)",
    borderColor: "var(--border)",
  },
  chipCelebration: {
    color: "#E091C0",
    borderColor: "rgba(224,145,192,0.4)",
    background: "rgba(224,145,192,0.06)",
  },
  noteText: {
    margin: 0,
    fontSize: 11,
    color: "var(--muted)",
    fontStyle: "italic",
  },
  cardErrorMsg: {
    margin: 0,
    fontSize: 11,
    color: "#FF8A7A",
    fontWeight: 600,
  },
  cardActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  primaryBtn: {
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid var(--gold)",
    background: "var(--gold)",
    color: "#0D0B08",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  seatBtn: {
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid #4CAF50",
    background: "rgba(76,175,80,0.1)",
    color: "#4CAF50",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  moveBtn: {
    height: 32,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid #FF9800",
    background: "rgba(255,152,0,0.1)",
    color: "#FF9800",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
  },
  ghostBtn: {
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 8,
    border: "1px solid var(--border)",
    background: "transparent",
    color: "var(--muted)",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  seatPicker: {
    marginTop: 6,
    padding: "10px",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  seatPickerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  seatPickerTitle: {
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--muted)",
  },
  seatPickerClose: {
    background: "transparent",
    border: 0,
    color: "var(--muted)",
    fontSize: 18,
    cursor: "pointer",
    padding: "0 4px",
    lineHeight: 1,
  },
  tableGroup: { display: "flex", flexDirection: "column", gap: 4 },
  tableGroupLabel: {
    margin: 0,
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "var(--muted)",
  },
  tableGrid: { display: "flex", flexWrap: "wrap", gap: 6 },
  tableChip: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid #4CAF50",
    background: "rgba(76,175,80,0.08)",
    color: "#4CAF50",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    gap: 2,
  },
  tableCapacity: {
    fontSize: 9,
    fontWeight: 600,
    color: "var(--muted)",
  },
};
